import { Box, Typography, Skeleton } from "@mui/material";
import { useTranslation } from "react-i18next";

import { Entity } from "../../../../../../types/types";

import { useEntityDataFormatter } from "../../../../../Entity/hooks/useEntityDataFormatter";

import {
  EntityCountryIcon,
  EntityFoundationIcon,
  EntityNationalLevelIcon,
  EntityTypeIcon,
} from "../../../../assets/icons/entity-icons";

interface InfoCardsProps {
  entity?: Entity;
  isLoading: boolean;
}

const InfoCards = ({ entity, isLoading }: InfoCardsProps) => {
  const { entityLevel, countryName, role, foundedYear } = useEntityDataFormatter(entity);

  const infoCards = [
    { title: "type", value: role, bgColor: "#E1F7E3" },
    { title: "entityLevel", value: entityLevel, bgColor: "#F5F7FA" },
    { title: "country", value: countryName, bgColor: "#EBEBFF" },
    { title: "foundedYear", value: foundedYear, bgColor: "#F9F5FF" },
  ];

  if (isLoading) {
    return (
      <Box sx={styles.container}>
        {[1, 2, 3, 4].map((index) => (
          <Box key={index} sx={styles.infoCardContainer}>
            <Skeleton variant="rounded" width={52} height={52} />
            <Box sx={{ display: "flex", flexDirection: "column", gap: "6px", flex: 1 }}>
              <Skeleton variant="text" width={120} height={24} />
              <Skeleton variant="text" width={80} height={20} />
            </Box>
          </Box>
        ))}
      </Box>
    );
  }

  return (
    <Box sx={styles.container}>
      {infoCards.map((card) => (
        <InfoCard key={card.title} {...card} />
      ))}
    </Box>
  );
};

export default InfoCards;

const InfoCard = ({ title, value, bgColor }: { title: string; value: string; bgColor: string }) => {
  const { t } = useTranslation("profiles", {
    keyPrefix: "generalInfo",
  });

  const CardIconMapper = {
    type: <EntityTypeIcon />,
    country: <EntityCountryIcon />,
    entityLevel: <EntityNationalLevelIcon />,
    foundedYear: <EntityFoundationIcon />,
  };

  return (
    <Box sx={styles.infoCardContainer}>
      <Box sx={styles.infoCardIconContainer(bgColor)}>{CardIconMapper[title as keyof typeof CardIconMapper]}</Box>
      <Box sx={styles.infoCardContentContainer}>
        <Typography sx={styles.infoCardValue}>{t(title)}</Typography>
        <Typography sx={styles.infoCardTitle}>{value ?? "--"}</Typography>
      </Box>
    </Box>
  );
};

const styles = {
  container: { display: "grid", gridTemplateColumns: { xs: "repeat(1, 1fr)", sm: "repeat(2, 1fr)" }, gap: "16px" },
  infoCardContainer: { display: "flex", gap: "16px", alignItems: "center", gridColumn: "span 1" },
  infoCardIconContainer: (bgColor: string) => ({ backgroundColor: bgColor, borderRadius: "4px", padding: "14px" }),
  infoCardContentContainer: { display: "flex", flexDirection: "column", gap: "6px" },
  infoCardValue: {
    color: "var(--Gray-700, #4E5566)",
    fontFamily: "Montserrat",
    fontSize: "14px",
    fontStyle: "normal",
    fontWeight: 400,
    lineHeight: "20px",
  },
  infoCardTitle: {
    color: "var(--Gray-900, #1D2026)",
    fontFamily: "Montserrat",
    fontSize: "16px",
    fontStyle: "normal",
    fontWeight: 600,
    lineHeight: "24px",
  },
};
